/**
 * Расчёт сделки портфеля: PnL по цене, фандинг (факт или оценка), комиссии.
 *
 * Жил внутри pages/Portfolio.tsx, а пользуется им ещё и панель «Портфель» на
 * графике (ChartPanel). Из-за одного импорта функции вся страница портфеля
 * (~60 КБ) попадала в чанк терминала и грузилась вместе с ним. Здесь — только
 * чистая математика и типы, без React и без словарей.
 */

export interface Trade {
  id: number;
  symbol: string;
  long_ex: string;
  short_ex: string;
  long_entry: number;
  short_entry: number;
  qty: number;
  note?: string | null;
  created_at?: string;
  linked?: boolean;   // импортирована с биржи (авто-синк qty + авто-закрытие)
  // Кросс-листинговый хедж (SKHYNIX↔SKHY): шорт-нога — другой инструмент.
  short_symbol?: string | null;
  cross_ratio?: number | null;   // price(symbol) = ratio × price(short_symbol)
  short_qty?: number | null;     // количество шорт-ноги в ЕЁ монетах
  // Закрытие (история)
  closed?: boolean;
  close_long?: number | null;
  close_short?: number | null;
  close_spread?: number | null;
  realized_price_pnl?: number | null;
  realized_funding?: number | null;
  realized_total?: number | null;
  realized_fees?: number | null;   // комиссии вход+выход по ставкам пользователя
  // Итог сверен с историей бирж по ногам (trade_facts на сервере): exchange —
  // обе ноги, partial — одна, none — истории нет, цифры остались расчётными.
  realized_fact?: { status?: 'exchange' | 'partial' | 'none'; note?: string | null } | null;
  realized_total_est?: number | null;   // итог до сверки — показываем в подсказке
  closed_at?: string | null;
}

export interface LegQuote {
  mid?: number | null;
  index_price?: number | null;
  funding_rate?: number | null;      // доля за один интервал (0.0003 = 0.03%)
  funding_interval_h?: number | null;
  next_funding_ts?: number | null;
  min_amount?: number | null;        // мин. размер ордера, base-юниты (ccxt)
  min_cost?: number | null;          // мин. ноционал, USDT (ccxt; часто null)
  contract_size?: number | null;
  amount_step?: number | null;
  tick_size?: number | null;         // шаг цены — по нему округляем как биржа
}

/** created_at (naive-UTC от backend) → epoch ms, либо null. */
export function tradeCreatedMs(createdAt?: string | null): number | null {
  if (!createdAt) return null;
  // backend пишет naive-UTC (datetime.utcnow) без 'Z' → форсируем UTC.
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(createdAt) ? createdAt : createdAt + 'Z';
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Кол-во прошедших funding-сеттлментов в окне (открытие, сейчас].
 *
 * Биржа списывает фандинг в ФИКСИРОВАННЫЕ моменты по сетке (00:00/08:00/16:00 …),
 * а не «через интервал после открытия». Поэтому считаем реальные точки сеттлмента
 * S_k = nextFundingTs − k·интервал и берём те, что попали в (createdMs, now].
 * Формула инвариантна к сдвигу nextFundingTs на целые интервалы (можно брать как
 * прошлый, так и будущий сеттлмент — выравнивание то же). Фолбэк на удержание,
 * если якоря next_funding_ts нет.
 */
function _fundingCycles(
  createdMs: number | null,
  nextFundingTs: number | null | undefined,
  intervalH: number,
  nowMs: number,
): number {
  const intervalMs = intervalH * 3_600_000;
  if (!(intervalMs > 0) || createdMs == null) return 0;
  if (nextFundingTs && nextFundingTs > 0) {
    const c =
      Math.floor((nowMs - nextFundingTs) / intervalMs) -
      Math.floor((createdMs - nextFundingTs) / intervalMs);
    return Math.max(0, c);
  }
  // нет точки сеттлмента → грубо по длительности удержания
  return Math.max(0, Math.floor((nowMs - createdMs) / intervalMs));
}

/**
 * Чистый расчёт по сделке. `longLeg`/`shortLeg` — записи из /api/funding
 * (mid + funding_rate + funding_interval_h). Возвращает цену-PnL И фандинг:
 *   LONG  платит фандинг при rate>0  → accrued = −rate × notional × циклы
 *   SHORT получает фандинг при rate>0 → accrued = +rate × notional × циклы
 */
export interface RealFunding {
  /** Факт по каждой ноге: сумма выплат биржи с момента открытия сделки. */
  long?: number | null;
  short?: number | null;
  /** Биржи, где историю фандинга мы не читаем — там останется оценка. */
  longEstimated?: boolean;
  shortEstimated?: boolean;
  /** По ноге взят «реализованный» с самой позиции: комиссию входа биржа
   *  в нём уже вычла — второй раз её не считаем. */
  longHasFees?: boolean;
  shortHasFees?: boolean;
}

/** Ставки тейкер-комиссий ног, % за сторону (своя из настроек или дефолт). */
export interface LegFees { long: number; short: number; }

export function computeTrade(t: Trade, longLeg?: LegQuote | null, shortLeg?: LegQuote | null,
                             nowMs?: number, real?: RealFunding | null, fees?: LegFees | null) {
  const longNow = longLeg?.mid ?? null;
  const shortNow = shortLeg?.mid ?? null;
  // Кросс-хедж: цены ног в разных единицах (1 SKHYNIX = 10 SKHY) — для спреда
  // шорт приводим к масштабу лонга, PnL считаем по СВОИМ qty каждой ноги.
  const ratio = t.cross_ratio && t.cross_ratio > 0 ? t.cross_ratio : 1;
  const shortQty = t.short_qty && t.short_qty > 0 ? t.short_qty : t.qty;
  const shortEntryAdj = t.short_entry * ratio;
  const entryMid = (t.long_entry + shortEntryAdj) / 2;
  const entrySpread = entryMid > 0 ? (shortEntryAdj - t.long_entry) / entryMid * 100 : null;
  let curSpread: number | null = null, pricePnl: number | null = null, pnlPct: number | null = null;
  if (longNow != null && shortNow != null && longNow > 0 && shortNow > 0) {
    const shortNowAdj = shortNow * ratio;
    const curMid = (longNow + shortNowAdj) / 2;
    curSpread = (shortNowAdj - longNow) / curMid * 100;
    pricePnl = t.qty * (longNow - t.long_entry) + shortQty * (t.short_entry - shortNow);
    const notional = t.qty * entryMid;
    pnlPct = notional > 0 ? pricePnl / notional * 100 : null;
  }

  // ---- funding ----
  const now = nowMs ?? Date.now();
  const createdMs = tradeCreatedMs(t.created_at);
  const notL = t.qty * t.long_entry;
  const notS = t.qty * t.short_entry;
  const rL = longLeg?.funding_rate, ihL = longLeg?.funding_interval_h;
  const rS = shortLeg?.funding_rate, ihS = shortLeg?.funding_interval_h;
  let cyclesLong = 0, cyclesShort = 0;
  let accLong: number | null = null, accShort: number | null = null;
  let nextLong: number | null = null, nextShort: number | null = null;
  // Прогноз — фандинг за сутки (нетто). cycles/day = 24/интервал.
  let perDayLong: number | null = null, perDayShort: number | null = null;
  if (rL != null && ihL && ihL > 0) {
    // циклы — по реальным сеттлментам биржи (сетка), а не по длительности удержания
    cyclesLong = _fundingCycles(createdMs, longLeg?.next_funding_ts, ihL, now);
    accLong = -Number(rL) * notL * cyclesLong;   // long платит при rate>0
    nextLong = -Number(rL) * notL;
    perDayLong = -Number(rL) * notL * (24 / ihL);
  }
  if (rS != null && ihS && ihS > 0) {
    cyclesShort = _fundingCycles(createdMs, shortLeg?.next_funding_ts, ihS, now);
    accShort = Number(rS) * notS * cyclesShort;   // short получает при rate>0
    nextShort = Number(rS) * notS;
    perDayShort = Number(rS) * notS * (24 / ihS);
  }
  // ФАКТ важнее оценки: биржа знает, сколько реально списала. Оценку
  // (ставка × ноционал × циклы) оставляем только там, где истории нет —
  // phemex/aster её не отдают, и на свежей сделке выплат ещё не было.
  const realLong = real?.long, realShort = real?.short;
  const accLongUsed = realLong != null ? realLong : accLong;
  const accShortUsed = realShort != null ? realShort : accShort;
  // Три состояния, а не два: по одной ноге факт может быть, а по другой нет
  // (phemex/aster историю фандинга не отдают). Называть такую сумму «фактом»
  // нельзя — она неполная, и это надо видеть, а не выяснять вычитанием.
  const fundSource: 'fact' | 'partial' | 'estimate' =
    (realLong != null && realShort != null) ? 'fact'
    : (realLong != null || realShort != null) ? 'partial'
    : 'estimate';
  const fundAccrued = (accLongUsed != null || accShortUsed != null)
    ? (accLongUsed ?? 0) + (accShortUsed ?? 0) : null;
  const fundNext = (nextLong != null || nextShort != null) ? (nextLong ?? 0) + (nextShort ?? 0) : null;
  const fundPerDay = (perDayLong != null || perDayShort != null) ? (perDayLong ?? 0) + (perDayShort ?? 0) : null;
  // ---- комиссии: вход + выход на каждой ноге, по ставкам пользователя ----
  // Без них «итого» было грязным: на круг это четыре тейкера, и на узком
  // спреде именно они решают, в плюсе сделка или в минусе.
  let feesEntry: number | null = null, feesExit: number | null = null;
  if (fees) {
    const fL = fees.long / 100, fS = fees.short / 100;
    feesEntry = (real?.longHasFees ? 0 : t.qty * t.long_entry * fL)
              + (real?.shortHasFees ? 0 : shortQty * t.short_entry * fS);
    // Выход — по текущим ценам (если их нет — по ценам входа).
    feesExit = t.qty * (longNow ?? t.long_entry) * fL + shortQty * (shortNow ?? t.short_entry) * fS;
  }
  const feesTotal = feesEntry != null && feesExit != null ? feesEntry + feesExit : null;
  const total = (pricePnl ?? 0) + (fundAccrued ?? 0) - (feesTotal ?? 0);

  return {
    feesEntry, feesExit, fees: feesTotal,
    entrySpread, curSpread, pnl: pricePnl, pnlPct,
    longNow, shortNow,
    fundAccrued, fundNext, fundPerDay, perDayLong, perDayShort,
    accLong: accLongUsed, accShort: accShortUsed, fundSource,
    nextLong, nextShort, cyclesLong, cyclesShort,
    total: (pricePnl == null && fundAccrued == null) ? null : total,
  };
}
